import { useContext, useState } from 'react';
import { Alert, Button, Card, Col, Container, Image, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import Sidebar from './Sidebar';
import Breadcrumb from './Breadcrumb';
import useDiscoveryFetch from './components/Discovery/useDiscoveryFetch';
import MobileDiscoveryHeading from './components/Discovery/MobileDiscoveryHeading';
import CommunityDesktopTable from './components/Communities/CommunityDesktopTable';
import CommunityMobileList from './components/Communities/CommunityMobileList';
import useMobileLoadMore from './components/Discovery/useMobileLoadMore';
import MobileLoadMoreFooter from './components/Discovery/MobileLoadMoreFooter';
import CommunityPaginationRow from './components/Communities/CommunityPaginationRow';
import icEmptyBox from './assets/img/ic-empty-box.svg';

// Communities list (Phase A). A single GET on mount — no aggressive polling
// here; the list refreshes itself whenever this page is (re)mounted.
const Communities = () => {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    // Same mobile breakpoint as the Discovery list pages.
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const openCommunity = (c) => navigate(`/communities/${c._id}`);

    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchConversations = async () => {
            try {
                if (!isReconnect) setLoading(true);
                setError('');
                const res = await api.get('/api/conversations', { signal });
                if (!isCurrent()) return;
                setConversations(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching communities:', err);
                setError('An error occurred while loading your communities.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchConversations();
    }, []);

    // Client-side pagination over the already fetched list (desktop only).
    const totalPages = Math.max(1, Math.ceil(conversations.length / rowsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const currentItems = conversations.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);

    // Mobile: reveal the fetched list 10 at a time (shared Discovery hook, no refetch).
    const mobilePagination = useMobileLoadMore({
        items: conversations,
        filterKey: 'communities',
        storageKey: 'communities-mobile-list',
        isMobile,
    });

    // Shared loading / error / empty handling; `list` is the view-specific body.
    const renderState = (list, stateClassName) => {
        const wrap = (node) => (stateClassName ? <div className={stateClassName}>{node}</div> : node);
        if (loading) {
            return wrap(
                <div className="py-5 text-center">
                    <Spinner animation="border" />
                </div>
            );
        }
        if (error) return wrap(<Alert variant="danger" className="mt-3">{error}</Alert>);
        if (conversations.length === 0) {
            // Same structure/classes as the /support empty state (SupportList).
            const empty = (
                <Row>
                    <Col
                        md={isMobile ? undefined : 6}
                        className={`no-result${isMobile ? '' : ' offset-md-3'} d-flex flex-column justify-content-center align-items-center text-center`}
                    >
                        <Image src={icEmptyBox} alt="No communities" className="mb-4" />
                        <h2 className="mb-3">No communities yet</h2>
                        <p className="mb-4">Join a Country or City community from Discovery to start chatting with other students there.</p>
                        <div className="d-flex flex-wrap justify-content-center gap-3">
                            <Button variant="primary" onClick={() => navigate('/discovery/countries')}>
                                Browse countries
                            </Button>
                            <Button variant="outline-secondary" onClick={() => navigate('/discovery/cities')}>
                                Browse cities
                            </Button>
                        </div>
                    </Col>
                </Row>
            );
            // Desktop already sits inside the page card; mobile has none, so give
            // the empty state one (as /support does) for the .no-result spacing.
            return isMobile
                ? wrap(<Card className="mb-4"><Card.Body>{empty}</Card.Body></Card>)
                : empty;
        }
        return list;
    };

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1 communities-page">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            {isMobile ? (
                                <>
                                    <MobileDiscoveryHeading title="Communities" />
                                    {renderState(
                                        <CommunityMobileList
                                            conversations={mobilePagination.mobileItems}
                                            currentUserId={user?._id}
                                            onOpen={(c) => {
                                                mobilePagination.rememberScrollPosition();
                                                openCommunity(c);
                                            }}
                                        >
                                            <MobileLoadMoreFooter
                                                visibleCount={mobilePagination.visibleCount}
                                                totalCount={mobilePagination.totalCount}
                                                itemLabel="communities"
                                                onLoadMore={mobilePagination.loadMore}
                                                isLoading={mobilePagination.isLoadingMore}
                                                hasMore={mobilePagination.hasMore}
                                            />
                                        </CommunityMobileList>,
                                        'mobile-community-state'
                                    )}
                                </>
                            ) : (
                                // Same shell as /support: Card > Card.Body > title, table, pagination.
                                <Card>
                                    <Card.Body>
                                        <h2 className="mb-4">Communities</h2>
                                        {renderState(
                                            <>
                                                <Row>
                                                    <Col>
                                                        <CommunityDesktopTable
                                                            conversations={currentItems}
                                                            currentUserId={user?._id}
                                                            onOpen={openCommunity}
                                                        />
                                                    </Col>
                                                </Row>
                                                <CommunityPaginationRow
                                                    total={conversations.length}
                                                    currentPage={safePage}
                                                    totalPages={totalPages}
                                                    rowsPerPage={rowsPerPage}
                                                    onPageChange={setCurrentPage}
                                                    onRowsPerPageChange={(n) => {
                                                        setRowsPerPage(n);
                                                        setCurrentPage(1);
                                                    }}
                                                />
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default Communities;
